import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  issueViewerPrimaryQueryKey,
  preheatIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {getQueryClient} from '@github-ui/query-client'
import {useRelayCachePreheatMode} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {
  useCachePreheatQueries,
  useCachePreheatQuery,
  useCachePreheatTask,
  useCachePreheatTasks,
} from '@github-ui/relay-query-cache/hooks/use-cache-preheat-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useMemo} from 'react'

import {issueViewerSecondaryViewQueryOptions} from '../components/fetch-issue-viewer-secondary-view'
import {buildIssueViewerViewQueryVariables} from '../components/issue-viewer-view-query-variables'
import {IssueViewerViewGraphqlQuery, preheatIssueViewerSecondaryComponents} from '../components/IssueViewer'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  source: string
  allowedOwner?: string
  enabled?: boolean
  /** Override when the destination differs from the host viewer, such as a link opening a Relay side panel. */
  target?: 'full-page' | 'side-panel'
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
  }>
  source: string
  target?: UseCachePreheatIssueViewerProps['target']
}

function useHasDataRouterTarget(target: UseCachePreheatIssueViewerProps['target']) {
  const mode = useRelayCachePreheatMode()
  return target ? target === 'full-page' && isFeatureEnabled('issue_viewer_paved_path') : mode === 'tanstack'
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  source,
  allowedOwner,
  enabled = true,
  target,
}: UseCachePreheatIssueViewerProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const hasDataRouterTarget = useHasDataRouterTarget(target)
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const primaryVariables = useMemo(
    () => buildIssueViewerViewQueryVariables({owner, repo, number, allowedOwner}),
    [owner, repo, number, allowedOwner],
  )

  const secondaryQueryOptions = useMemo(
    () =>
      issueViewerSecondaryViewQueryOptions({
        owner,
        repo,
        number,
        markAsRead: false,
        sendAnalyticsEvent,
        preheat: true,
        preheatSource: source,
      }),
    [number, owner, repo, sendAnalyticsEvent, source],
  )
  const preheatSecondaryQuery = useCallback(
    () => getQueryClient().prefetchQuery(secondaryQueryOptions),
    [secondaryQueryOptions],
  )
  const primaryQueryKey = useMemo(() => issueViewerPrimaryQueryKey({owner, repo, number}), [owner, repo, number])
  const preheatPrimaryQuery = useCallback(
    () => preheatIssueViewerPrimary({owner, repo, number, source}),
    [owner, repo, number, source],
  )

  useEffect(() => {
    if (!enabled) return
    void preheatIssueViewerSecondaryComponents()
  }, [enabled])

  useCachePreheatQuery({
    query: IssueViewerViewGraphqlQuery,
    variables: primaryVariables,
    source,
    excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
    highPriority: true,
    enabled: enabled && !hasDataRouterTarget,
  })

  useCachePreheatTask({
    key: JSON.stringify(primaryQueryKey),
    task: preheatPrimaryQuery,
    enabled: enabled && hasDataRouterTarget,
    highPriority: true,
  })

  useCachePreheatTask({
    key: JSON.stringify(secondaryQueryOptions.queryKey),
    task: preheatSecondaryQuery,
    enabled,
  })
}

export function useCachePreheatIssueViewerQueries({issues, source, target}: UseCachePreheatIssueViewerQueriesProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const hasDataRouterTarget = useHasDataRouterTarget(target)
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const preheatQueries = useMemo(
    () =>
      issues.map(({owner, repo, number}) => ({
        query: IssueViewerViewGraphqlQuery,
        variables: buildIssueViewerViewQueryVariables({owner, repo, number}),
        source,
        excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
        highPriority: true,
        enabled: !hasDataRouterTarget,
      })),
    [issues, source, excludeFromPrimaryQueryCacheKey, hasDataRouterTarget],
  )

  useEffect(() => {
    if (issues.length === 0) return
    void preheatIssueViewerSecondaryComponents()
  }, [issues.length])

  const secondaryPreheatTasks = useMemo(
    () =>
      issues.map(({owner, repo, number}) => {
        const options = issueViewerSecondaryViewQueryOptions({
          owner,
          repo,
          number,
          markAsRead: false,
          sendAnalyticsEvent,
          preheat: true,
          preheatSource: source,
        })
        return {
          key: JSON.stringify(options.queryKey),
          task: () => getQueryClient().prefetchQuery(options),
        }
      }),
    [issues, sendAnalyticsEvent, source],
  )

  const primaryPreheatTasks = useMemo(
    () =>
      hasDataRouterTarget
        ? issues.map(issue => ({
            key: JSON.stringify(issueViewerPrimaryQueryKey(issue)),
            task: () => preheatIssueViewerPrimary({...issue, source}),
            highPriority: true,
          }))
        : [],
    [issues, source, hasDataRouterTarget],
  )

  useCachePreheatQueries({queries: preheatQueries})
  useCachePreheatTasks({tasks: primaryPreheatTasks})
  useCachePreheatTasks({tasks: secondaryPreheatTasks})
}
